/**
 * @author Mevan Nirosh
 * @version 0.2
 * @since 12/8/2025
 */
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react"
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Fab,
  Stack,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material"
import Alerts from "../../../../../../base/errorHandeling/Alerts"
import { MaterialReactTable, useMaterialReactTable } from "material-react-table"
import { DemoContainer } from "@mui/x-date-pickers/internals/demo"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import api from "../../../../../../base/services/ResApi"
import { FetchErrorHandeling } from "../../../../../../base/errorHandeling/FetchErrorHandeling"
import dayjs from "dayjs"
import FormControl from "@mui/material/FormControl"
import TroubleshootIcon from "@mui/icons-material/Troubleshoot"
import DownloadIcon from "@mui/icons-material/Download"

const OldLogs = ({ serialNo, onClose }) => {
  const [rowCount, setRowCount] = useState(0)
  const [data, setData] = useState([])
  const [dataLoading, setDataLoading] = useState(false)

  const [fromDate, setFromDate] = useState(dayjs())
  const [toDate, setToDate] = useState(dayjs())
  const [isSearch, setIsSearch] = useState(false)

  // for error handling
  const [iserror, setIserror] = useState(false)
  const [iserrorShow, setIserrorShow] = useState(false)
  const [isUnauhtError, setIsUnauhtError] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])

  //for success messages
  const [isSuccess, setIsSuccess] = useState(false)
  const [successMessages, setSuccessMessages] = useState([])

  const dateFilterFormat = "DD/MM/YYYY"

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  //clear all alerts after a timeout
  useEffect(() => {
    if ((iserror || isSuccess) === true) {
      let timerId = setTimeout(() => {
        setIserror(false)
        setErrorMessages([])
        setIsSuccess(false)
        setSuccessMessages([])
        timerId = null
      }, 5000)
      return () => clearTimeout(timerId)
    }
  }, [iserror, isSuccess])

  // -----------------------------
  // Polling refs (prevents repeated download + multiple intervals)
  // -----------------------------
  const intervalRef = useRef(null)
  const inFlightRef = useRef(false)
  const downloadedRef = useRef(false)
  const activeLogReqIdRef = useRef(null)

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    inFlightRef.current = false
    activeLogReqIdRef.current = null
  }, [])

  useEffect(() => {
    // cleanup on unmount
    return () => stopPolling()
  }, [stopPolling])

  const columns = useMemo(
    () => [
      {
        accessorKey: "serialNo",
        header: "Serial No",
      },
      {
        accessorKey: "appLabel",
        header: "App Label",
      },
      {
        accessorKey: "logType",
        header: "Log Type",
      },
      {
        accessorKey: "formDateTime",
        header: "From Date/Time",
      },
      {
        accessorKey: "toDateTime",
        header: "To Date/Time",
      },
    ],
    [],
  )

  // -----------------------------
  // Data fetch (pagination + search)
  // -----------------------------
  useEffect(() => {
    if (isSearch || pagination.pageIndex || pagination.pageSize) {
      setDataLoading(true)

      const config = {
        headers: {
          "Is-Pagination": true,
          "Page-Number": pagination.pageIndex + 1,
          "Page-Size": pagination.pageSize,
          "Sort-Column": "tmnl_log_id",
          "Sort-Order": "ASC",
          SN: serialNo,
          FDT: dayjs(fromDate).format(dateFilterFormat),
          TDT: dayjs(toDate).format(dateFilterFormat),
          Action: "VIEW_ALL_TERMINAL_LOG",
        },
      }

      api
        .get("/terminal/logs/all", config)
        .then((res) => {
          setIsSearch(false)
          setData(res.data?.dataList || [])
          setRowCount(res.data?.total || 0)
          setDataLoading(false)
          setErrorMessages([])
          setIserror(false)
          setIsUnauhtError(false)
        })
        .catch((error) => {
          setIsSearch(false)
          setDataLoading(false)
          FetchErrorHandeling(
            error,
            setIserror,
            setErrorMessages,
            setIserrorShow,
            setIsUnauhtError,
          )
        })
    }
  }, [isSearch, isUnauhtError, pagination.pageIndex, pagination.pageSize]) // keep as you had

  // -----------------------------
  // Download helper
  // -----------------------------
  const downloadZipFile = (binaryData, fileName) => {
    const blob = new Blob([binaryData], { type: "application/octet-stream" })
    const url = URL.createObjectURL(blob)

    const link = document.createElement("a")
    link.href = url
    link.download = fileName

    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleDownloadLog = useCallback(
    (logReqId) => {
      // Stop any existing polling first (prevents multiple intervals)
      stopPolling()

      // Reset guards for a new request
      downloadedRef.current = false
      inFlightRef.current = false
      activeLogReqIdRef.current = logReqId

      const logDownloadConfig = {
        headers: {
          "Log-Request-Id": logReqId,
          Action: "DOWNLOAD_OLD_LOG",
        },
        responseType: "blob",
      }

      const pollOnce = async () => {
        // Only poll if still the active request
        if (activeLogReqIdRef.current !== logReqId) return

        // Prevent overlapping calls
        if (inFlightRef.current || downloadedRef.current) return

        inFlightRef.current = true

        try {
          const res = await api.get("/terminal/logs", logDownloadConfig)

          // Blob can be "truthy" even if it's empty -> check size
          const blob = res?.data
          const isReady =
            res?.status === 200 && blob instanceof Blob && blob.size > 0

          if (isReady) {
            downloadedRef.current = true

            // Stop interval BEFORE download to avoid race conditions
            stopPolling()

            downloadZipFile(blob, `${serialNo}.zip`)
            setIsSuccess(true)
            setSuccessMessages(["Log downloaded successfully."])
          }
        } catch (error) {
          stopPolling()
          await FetchErrorHandeling(
            error,
            setIserror,
            setErrorMessages,
            setIserrorShow,
            setIsUnauhtError,
          )
        } finally {
          inFlightRef.current = false
        }
      }

      // Run immediately once, then poll every 10s
      pollOnce()
      intervalRef.current = setInterval(pollOnce, 10000)
    },
    [serialNo, stopPolling],
  )

  const handleSearch = () => {
    setIsSearch(true)
  }

  const handleFromChange = (newValue) => {
    setFromDate(newValue)
  }
  const handleToChange = (newValue) => {
    setToDate(newValue)
  }

  const table = useMaterialReactTable({
    columns,
    data: data ?? [],
    state: {
      showProgressBars: dataLoading,
      pagination,
    },
    getRowId: (row) => row.id,
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["mrt-row-actions"],
      },
    },
    manualPagination: true,
    onPaginationChange: setPagination,
    muiToolbarAlertBannerProps: false,
    rowCount: rowCount,
    muiPaginationProps: { rowsPerPageOptions: [10] },
    enableColumnPinning: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableRowActions: true,
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Tooltip arrow placement="left" title="Download Log">
          <IconButton onClick={() => handleDownloadLog(row.original.logReqId)}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  })

  return (
    <div>
      <DialogContent dividers>
        <Stack direction="row" spacing={3}>
          <FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  value={dayjs(fromDate)}
                  onChange={handleFromChange}
                  label="From"
                />
                <DatePicker
                  value={dayjs(toDate)}
                  onChange={handleToChange}
                  label="To"
                />
              </DemoContainer>
            </LocalizationProvider>
          </FormControl>

          <div style={{ marginTop: 12 }}>
            <Fab onClick={handleSearch} variant="extended" color="primary">
              <TroubleshootIcon sx={{ mr: 1 }} />
              Search
            </Fab>
          </div>
        </Stack>

        <br />
        <br />
        <div>
          <Alerts
            iserror={iserror}
            errorMessages={errorMessages}
            isSuccess={isSuccess}
            successMessages={successMessages}
            iserrorShow={iserrorShow}
          />
        </div>
        <br />

        <Box sx={{ display: "grid" }}>
          <MaterialReactTable table={table} />
        </Box>

        <br />
        <br />
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: "1.25rem" }}>
        <Button
          onClick={() => {
            stopPolling() // stop polling if user closes dialog
            onClose?.()
          }}
          variant="outlined"
          color="error"
        >
          Cancel
        </Button>
      </DialogActions>
    </div>
  )
}

export default OldLogs
